import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Monitor, Smartphone, Globe, Shield, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SessoesAtivas = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/auth/sessions`, { withCredentials: true });
      setSessions(res.data || []);
    } catch (err) {
      console.error('Erro ao carregar sessões:', err);
      toast.error('Não foi possível carregar as sessões ativas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const handleRevokeSession = async (sessionId) => {
    try {
      setActionLoading(true);
      await axios.delete(`${API}/auth/sessions/${sessionId}`, { withCredentials: true });
      toast.success('Sessão encerrada com sucesso.');
      await loadSessions();
    } catch (err) {
      toast.error('Erro ao encerrar sessão.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeOthers = async () => {
    if (!window.confirm('Deseja realmente encerrar todas as outras sessões ativas?')) {
      return;
    }
    try {
      setActionLoading(true);
      const res = await axios.post(`${API}/auth/sessions/revoke-others`, {}, { withCredentials: true });
      toast.success(res.data?.message || 'Outras sessões encerradas.');
      await loadSessions();
    } catch (err) {
      toast.error('Erro ao encerrar outras sessões.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm('Deseja realmente sair de todos os dispositivos? Você precisará fazer login novamente.')) {
      return;
    }
    try {
      setActionLoading(true);
      await axios.post(`${API}/auth/sessions/revoke-all`, {}, { withCredentials: true });
      toast.success('Todas as sessões foram encerradas.');
      await logout();
      navigate('/');
    } catch (err) {
      toast.error('Erro ao encerrar todas as sessões.');
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] text-slate-900 dark:text-white flex flex-col font-sans transition-colors">
      <Header />

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
        
        {/* Back Link */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/perfil')}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-blue-900 dark:text-slate-400 dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={16} />
            <span>Meu perfil</span>
          </button>
        </div>

        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-blue-900 dark:text-white">
              Sessões ativas
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1.5">
              Gerencie as sessões ativas da sua conta.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleRevokeOthers}
              disabled={actionLoading || sessions.length <= 1}
              className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 shadow-sm dark:bg-white/10 dark:hover:bg-white/15 dark:border-white/10 dark:text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-xl transition-all disabled:opacity-50"
            >
              Encerrar outras sessões
            </button>
            <button
              onClick={handleRevokeAll}
              disabled={actionLoading}
              className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-xs sm:text-sm font-bold px-4 py-2.5 rounded-xl shadow-lg shadow-red-500/25 transition-all"
            >
              Sair de todos os dispositivos
            </button>
          </div>
        </div>

        {/* Sessions List Container */}
        <div className="mt-8 bg-white dark:bg-[#0B1528]/90 border border-blue-900/10 dark:border-blue-900/40 rounded-2xl p-4 sm:p-6 shadow-sm dark:shadow-2xl dark:shadow-black/40">
          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center gap-3 text-slate-500 dark:text-slate-400">
              <RefreshCw className="animate-spin text-blue-600 dark:text-cyan-400" size={24} />
              <p className="text-sm">Carregando sessões ativas...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="py-12 text-center text-slate-500 dark:text-slate-400">
              <Shield className="mx-auto mb-2 text-blue-600 dark:text-cyan-400 opacity-60" size={32} />
              <p className="text-sm">Nenhuma sessão ativa encontrada.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-white/10">
              {sessions.map((sess, idx) => (
                <div
                  key={sess.session_id || idx}
                  className={`py-4 first:pt-0 last:pb-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                    sess.is_current ? 'bg-blue-50/80 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 -mx-3 px-3 rounded-xl' : ''
                  }`}
                >
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 text-blue-600 dark:bg-blue-500/20 dark:border-cyan-400/20 dark:text-cyan-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                      {sess.device_info?.toLowerCase().includes('phone') || sess.device_info?.toLowerCase().includes('android') ? (
                        <Smartphone size={20} />
                      ) : (
                        <Monitor size={20} />
                      )}
                    </div>

                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 dark:text-white text-sm sm:text-base">
                          {sess.device_info || 'Navegador Web'}
                        </span>

                        {sess.is_current && (
                          <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/30 text-[11px] font-semibold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse"></span>
                            Sessão atual
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-slate-500 dark:text-slate-400 font-mono mt-1 tracking-tight">
                        {sess.ip_address} · {formatDate(sess.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Actions for other sessions */}
                  {!sess.is_current && (
                    <div className="flex items-center justify-end sm:justify-start">
                      <button
                        onClick={() => handleRevokeSession(sess.session_id)}
                        disabled={actionLoading}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:underline font-semibold text-xs sm:text-sm transition-colors py-1 px-2"
                      >
                        Encerrar
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Security Info Card */}
        <div className="mt-6 p-4 rounded-xl bg-blue-50/60 border border-blue-900/10 dark:bg-white/5 dark:border-white/10 flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400">
          <Shield className="text-blue-600 dark:text-cyan-400 flex-shrink-0" size={18} />
          <span>
            Caso você não reconheça alguma sessão listada, encerre-a imediatamente e altere sua senha para proteger sua conta.
          </span>
        </div>

      </main>
    </div>
  );
};
